<script setup lang="ts">
import type { ReportItemProps } from '@/types/appTypes'
import maxBy from 'lodash/maxBy'

const props = withDefaults(defineProps<ReportItemProps>(), {
  fromname: null,
  objects: () => [],
  hideVenuePeriod: false,
})

const app = useNuxtApp()
const storeIndex = useStoreIndex()

let aThreeLineContent = null // 3行見出しリスト
let sMuseumArticleSlug = null // ミュージアムスラッグ
let sEvent = null // 展覧会
let sVenue = null // 会場
let sPeriodLabel = null // 会期
let sPeriodRemain = null // 開催中情報
let tRelateImg = null // 関連画像(アイキャッチその２)
let sRelations = null
let sTitleEventNewest = ''

const isImReport = computed(() => {
  if (props.item.custom) {
    return props.item.custom.is_im_report === '1'
  }
  return false
})

const linkTo = computed(() => {
  return `/report/${props.item.article_slug}${props.fromname ? `?fromname=${props.fromname}` : ''}`
})

// 3行見出し追加
const addThreeLineContent = (sHeading, aThreeLineContent) => {
  if (sHeading) {
    aThreeLineContent.push(sHeading)
  }
}

// 関連記事からイベント取得
const eventItem = app.$getArticleRelationsSingle(props.item, 'event', null)

// 関連記事からイベント名
if (props.item?.custom?.event_article_slug) {
  const hitItems = app.$getArticleRelationsBySlug(props.item, props.item.custom.event_article_slug)
  if (hitItems?.length > 0) {
    sEvent = hitItems[0].title || null
  }
}

// 会場
if (!sVenue) {
  if (props.item && props.item.custom && props.item.custom.kaijo) {
    // レポートから
    sVenue = props.item.custom.kaijo
  } else if (eventItem) {
    // イベントから
    sVenue = app.$getEventVenue(eventItem, false)
    if (app.$isEventVenueMuseum(eventItem)) {
      if (!sMuseumArticleSlug) {
        sMuseumArticleSlug = app.$getEventMuseumArticleSlug(eventItem)
      } // ミュージアムスラッグ
    }
  }
}

// 会期
if (!sPeriodLabel) {
  if (props.item && props.item.custom && props.item.custom.kikan) {
    // アイテムから
    sPeriodLabel = props.item.custom.kikan // 会期
    // sPeriodRemain = null // 開催中情報
    if (eventItem && eventItem.day_count_display === 1) {
      sPeriodRemain = app.$getEventPeriodRemain(eventItem) // 開催中情報
    }
  } else if (eventItem) {
    // イベントから
    sPeriodLabel = app.$getEventPeriodLabel(eventItem) // 会期
    if (eventItem.day_count_display === 1) {
      sPeriodRemain = app.$getEventPeriodRemain(eventItem) // 開催中情報
    }
  }
}

if (!sRelations) {
  if (props.item?.relations) {
    sRelations = props.item.relations
    if (sRelations.length > 0) {
      const maxRelation = maxBy(sRelations, o => o.article.id)
      sTitleEventNewest = maxRelation.article.title || ''
    }
  }
}

// 読者レポートから3行見出し追加
if (!aThreeLineContent && props.item.custom && isImReport) {
  aThreeLineContent = []
  addThreeLineContent(props.item.custom.heading1, aThreeLineContent)
  addThreeLineContent(props.item.custom.heading2, aThreeLineContent)
  addThreeLineContent(props.item.custom.heading3, aThreeLineContent)
}

// 関連画像(アイキャッチその２)取得
if (!tRelateImg && props.item.custom?.picture2 && props.objects) {
  tRelateImg = storeIndex.getImgInfoByID(props.item.custom.picture2, props.objects, (props.item.is_old === 1) ? null : 'l')
}
</script>

<template>
  <div
    class="c-reportItem"
  >
    <div
      class="c-reportItem_content"
    >
      <div
        class="c-reportItem_content_title"
      >
        <div
          class="c-reportItem_content_title_type"
          v-bind:class="{ 'is-imReport': isImReport }"
        >
          <label
            class="c-reportItem_content_title_type_lable"
            v-text="isImReport ? 'IM': '読者'"
          />
          <label class="c-reportItem_content_title_type_lable">レポート</label>
        </div>
        <div class="c-reportItem_content_title_lable">
          <nuxt-link
            class="c-clampLines is-clampline3"
            v-bind:to="linkTo"
          >
            {{ item.title }}
          </nuxt-link>
        </div>
      </div>
      <div class="c-reportItem_content_image">
        <div class="c-reportItem_content_image_left">
          <nuxt-link
            class="u-hoverZoom"
            v-bind:to="linkTo"
          >
            <CommonImage
              v-bind:src="storeIndex.getArticleEyeCatchImageURL(item.eye_catch_image_object)"
              error-src="/images/noimage.svg"
              v-bind:alt="item.title"
              object-fit="cover"
              aspect-ratio="4 / 3"
              is-hover-effect
            />
          </nuxt-link>
        </div>
        <div class="c-reportItem_content_image_right">
          <div class="c-reportItem_content_image_right_high">
            <nuxt-link
              class="u-hoverZoom"
              v-bind:to="linkTo"
            >
              <CommonImage
                v-if="tRelateImg"
                class="c-reportItem_content_image_right_high_img"
                v-bind:src="tRelateImg.url"
                v-bind:alt="item.title"
                error-src="/images/noimage.svg"
                aspect-ratio="4 / 3"
                object-fit="contain"
                is-hover-effect
              />
            </nuxt-link>
          </div>
          <div class="c-reportItem_content_image_right_low">
            <div class="c-reportItem_content_image_right_low_clip">
              <Clip
                v-bind:mode="true"
                v-bind:dspcnt="true"
                v-bind:article-type-slug="item.article_type_slug"
                v-bind:article-slug="item.article_slug"
                v-bind:count="item.user_favorite_count"
              />
            </div>
            <div class="c-reportItem_content_image_right_low_sbutton">
              <ShareButtons />
            </div>
          </div>
        </div>
      </div>
      <div
        class="c-reportItem_content_comment"
      >
        <div class="c-reportItem_content_comment_html">
          <template v-if="isImReport">
            <div
              v-if="app.$isArticleOld(item)"
              class="c-reportItem_content_comment_html_im"
            >
              <div
                v-if="item && item.custom && item.custom.main_title"
                class="c-reportItem_content_comment_html_im_title c-clampLines is-clampline1"
              >
                {{ item.custom.main_title }}
              </div>
              <div
                v-if="item && item.custom && item.custom.sub_title"
                class="c-reportItem_content_comment_html_im_panel_old"
              >
                <div
                  class="c-clampLines is-clampline6"
                  v-html="app.$decodeHtmlEntities(item.custom.sub_title)"
                />
              </div>
            </div>
            <div
              v-else-if="!app.$isArticleOld(item) && aThreeLineContent.length > 0"
              class="c-reportItem_content_comment_html_im"
            >
              <div
                class="c-reportItem_content_comment_html_im_panel_new"
              >
                <div
                  v-for="(record, index) in aThreeLineContent"
                  v-bind:key="index"
                  class="c-reportItem_content_comment_html_im_panel_new_description"
                >
                  <div class="c-reportItem_content_comment_html_im_panel_new_description_iconWrap">
                    <v-icon class="c-reportItem_content_comment_html_im_panel_new_description_icon">
                      mdi-square-medium
                    </v-icon>
                  </div>
                  <div class="c-reportItem_content_comment_html_im_panel_new_description_text c-clampLines is-clampline2">
                    {{ record }}
                  </div>
                </div>
              </div>
            </div>
          </template>
          <template v-else>
            <div
              v-if="item && item.custom && item.custom.excerpt"
              class="c-reportItem_content_comment_html_area_panel c-clampLines is-clampline7"
              v-html="app.$trimEnter(item.custom.excerpt)"
            />
          </template>
          <div
            class="c-reportItem_content_comment_link"
          >
            <ArrowLink
              v-bind:to="`/report/${item.article_slug}`"
              label="レポートを読む"
              bold
            />
          </div>
        </div>
      </div>
      <table
        v-if="(sVenue || sPeriodLabel || sPeriodRemain) && !hideVenuePeriod"
        class="c-reportItem_content_table"
      >
        <tbody>
          <tr v-if="sEvent">
            <td
              class="c-reportItem_content_table_colFirst"
            >
              展覧会
            </td>
            <td>
              {{ sEvent }}
            </td>
          </tr>
          <tr
            v-if="sVenue"
          >
            <td
              class="c-reportItem_content_table_colFirst"
            >
              会場
            </td>
            <td>
              <nuxt-link
                v-if="sMuseumArticleSlug"
                v-bind:to="`/museum/${sMuseumArticleSlug}`"
                class="c-reportItem_content_table_city"
              >
                <div
                  v-html="sVenue"
                />
              </nuxt-link>
              <div
                v-else
                v-html="sVenue"
              />
            </td>
          </tr>
          <tr
            v-if="sPeriodLabel || sPeriodRemain"
          >
            <td
              class="c-reportItem_content_table_colFirst"
            >
              会期
            </td>
            <td>
              <div
                v-if="sPeriodLabel"
                class="c-reportItem_content_table_status_duration"
              >
                {{ sPeriodLabel }}
              </div>
              <div
                v-if="sPeriodRemain"
                class="c-reportItem_content_table_status_event"
                v-bind:style="{ color: sPeriodRemain.color }"
              >
                {{ sPeriodRemain.label }}
              </div>
            </td>
          </tr>
        </tbody>
      </table>
      <div class="c-reportItem_content_low">
        <nuxt-link
          v-bind:to="`/report/${item.article_slug}`"
        >
          <div
            class="c-reportItem_content_low_button"
          >
            <div class="c-reportItem_content_low_button_text">
              レポートを見る
            </div>
          </div>
        </nuxt-link>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
@use "@/assets/css/foundation/mixin/responsive";

.c-reportItem {
  width: 100%;
  min-height: fit-content;
  margin-top: 40px;
  background-color: var(--color_background_whiteblue);
  font-family: var(--g_font_family_NotoSansCJKjp);
  font-weight: normal;

  &_content {
    flex-direction: column;
    padding: 20px;
    font-weight: bold;

    @include responsive.mq_down(sp) {
      padding: 15px;
    }

    &_title {
      display: flex;
      width: 100%;

      @include responsive.mq_down(sp) {
        display: block;
      }

      &_type {
        display: flex;
        flex-direction: column;
        justify-content: center;
        min-width: 75px;
        max-width: 75px;
        height: 75px;
        margin-right: 15px;
        background-color: var(--color_report_author_bg_user);
        color: var(--g_color_text_white);
        font-size: 16px;

        @include responsive.mq_down(sp) {
          display: block;
          min-width: auto;
          max-width: 100%;
          height: auto;
          margin: 0 0 10px;
          padding: 10px;
          font-size: 0;
          line-height: 1.2;
        }

        &_lable {
          display: flex;
          flex-wrap: wrap;
          justify-content: center;
          font-weight: bold;

          @include responsive.mq_down(sp) {
            display: inline;
            font-size: 14px;
          }
        }

        &.is-imReport {
          background-color: var(--color_report_author_bg_im);
        }
      }

      &_lable {
        display: flex;
        flex: 1;
        align-items: center;
        margin-top: -3px;

        @include responsive.mq_down(sp) {
          margin: 20px 0;
          font-size: 18px;
        }

        > a {
          color: var(--color_text_base);
          font-size: 20px;
          font-weight: bold;
          line-height: 1.4;
        }
      }
    }

    &_image {
      display: flex;
      flex-direction: row;
      justify-content: space-between;
      width: 100%;
      margin-top: 13px;

      &_left {
        width: 63%;

        @include responsive.mq_down(sp) {
          width: 100%;
        }

        @include responsive.mq_down(small) {
          width: 58%;
        }
      }

      &_right {
        display: flex;
        flex-direction: column;
        justify-content: space-between;
        width: 37%;
        padding-left: 10px;
        gap: 12px;

        @include responsive.mq_down(sp) {
          display: none;
        }

        @include responsive.mq_down(small) {
          width: 42%;
        }

        &_high {
          width: 100%;

          &_img {
            width: 100%;
          }
        }

        &_low {
          display: flex;
          flex-direction: column;
          align-items: center;
          width: 100%;

          &_clip {
            margin-left: 36px;
          }

          &_sbutton {
            margin-top: 10px;
            margin-left: -20px;
          }
        }
      }
    }

    &_comment {
      display: flex;
      width: 100%;
      margin-top: 13px;
      color: var(--color_text_base);
      font-size: 14px;
      font-weight: 400;
      line-height: 1.444;

      &_html {
        width: 100%;

        &_im {
          &_title {
            font-family: var(--g_font_family_NotoSansCJKjp);
            font-size: 18px;
            font-weight: 500;
            font-weight: bold;
            line-height: 1.333;
          }

          &_panel_new {
            padding: 0;

            &_description {
              position: relative;
              margin: 0 0 10px;
              font-family: var(--g_font_family_NotoSansCJKjp);
              font-size: 15px;
              line-height: 1.3;

              &_iconWrap {
                position: absolute;
              }

              &_icon {
                color: var(--color_primary_link);
              }

              &_text {
                padding-left: 24px;
              }
            }
          }

          &_panel_old {
            padding: 8px 0 0;
            line-height: 1.5;
          }
        }

        &_area {
          &_panel {
            line-height: 1.5;
          }
        }
      }

      &_typeBold {
        position: relative;

        &_title {
          font-size: 18px;
          font-weight: bold;

          @include responsive.mq_down(sp) {
            font-size: var(--g_fontSize_h2_sp);
          }
        }

        &_listLable {
          margin-left: 14px;
          line-height: 1.7143;

          &_layoutSpace {
            width: 100%;
            height: 12px;
          }

          &_lable {
            font-weight: bold;

            &::before {
              content: " \2022";
              position: absolute;
              left: 5px;
            }
          }
        }
      }

      &_link {
        display: flex;
        justify-content: flex-end;
        width: 100%;
        margin-top: 15px;

        @include responsive.mq_up(pc) {
          display: none;
        }
      }
    }

    &_table {
      width: 680px;
      margin-top: 13px;
      margin-bottom: 2px;
      table-layout: fixed;
      border-collapse: collapse;
      background-color: var(--g_color_background_white);
      font-size: 14px;
      font-weight: 500;
      line-height: 1.714;

      @include responsive.mq_down(sp) {
        width: 100%;
      }

      tr td {
        height: 40px;
        padding-left: 20px;
        border: 1px solid var(--g_color_border_gray);
        vertical-align: middle;

        @include responsive.mq_down(sp) {
          height: auto;
          padding: 10px;
        }

        &:first-of-type {
          @include responsive.mq_down(sp) {
            width: 70px;
          }
        }
      }

      &_colFirst {
        width: 120px;
        background-color: var(--g_color_background_e7e7e7);
        font-weight: bold;
      }

      &_city {
        color: var(--color_primary_link);
      }

      &_status {
        display: flex;
        align-items: center;

        @include responsive.mq_down(sp) {
          flex-wrap: wrap;
        }

        &_duration {
          font-family: var(--g_font_family_NotoSansCJKjp);
          font-size: 14px;

          @include responsive.mq_down(sp) {
            flex-basis: 100%;
            margin-bottom: 5px;
          }
        }

        &_event {
          font-size: 14px;
        }

        &_border {
          position: relative;
          flex: 0 0 18px;
          width: 18px !important;
          height: 18px !important;
          margin-left: 10px;
          border: 1px solid var(--g_color_border_gray);
          border-radius: 50%;

          @include responsive.mq_down(sp) {
            margin-left: 0;
          }

          &_circle {
            position: absolute;
            top: 50%;
            left: 50%;
            width: 8px;
            height: 8px;
            transform: translate(-50%, -50%);
            border-radius: 50%;
          }
        }
      }
    }

    &_low {
      width: 100%;
      margin-top: 15px;

      @include responsive.mq_down(sp) {
        display: none;
      }

      &_button {
        display: flex;
        flex: 0 0 auto;
        align-items: center;
        justify-content: center;
        width: 100%;
        height: 42px;
        margin: 0;
        margin-left: auto;
        transition: 300ms;
        border: 1px solid var(--g_color_table_border_gray);
        border-color: var(--color_primary);
        background-color: var(--color_primary);
        color: #fff;
        text-decoration: none;
        cursor: pointer;

        @include responsive.mq_down(sp) {
          width: 100%;
          max-width: 189px;
        }

        @include responsive.mq_down(small) {
          width: 100%;
          max-width: 189px;
        }

        &:hover {
          opacity: 0.7;
        }

        &_icon {
          color: var(--color_primary_link);
          font-weight: bold;
        }

        &_text {
          color: #fff;
          font-size: 14px;
          font-weight: bold;
          line-height: 2.286;
        }
      }
    }
  }
}
</style>
